import React, { useEffect, useState } from 'react';
import { supabase } from '../App';

const DEMAND_MODEL_URL = process.env.REACT_APP_DEMAND_MODEL_URL || 'https://demand-forecast-model.hellocabradar.workers.dev';
const EVENT_CATEGORIES = [
  ['conferences', 'Conferences'],
  ['sports', 'Sports'],
  ['concerts', 'Concerts'],
  ['expos', 'Expos'],
  ['community', 'Community'],
  ['festivals', 'Festivals'],
  ['performing_arts', 'Performing arts']
];

const formatNumber = (value) => Number(value || 0).toLocaleString('en-GB');
const formatUplift = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;
const percentageOfPeak = (value, peak) => peak > 0 ? `${((Number(value || 0) / peak) * 100).toFixed(1)}%` : '0.0%';
const DEFAULT_EVENTS_PEAK = 200000;

const formatWeatherTimestamp = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).format(date);
};

const getDefaultDateRange = () => {
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + 13);
  const format = (date) => date.toISOString().split('T')[0];
  return { startDate: format(start), endDate: format(end) };
};

const londonDate = (value) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}).format(new Date(value));

// live_events stores PredictHQ categories, which use a hyphen for performing arts.
const categoryKey = (category) => String(category || '').trim().toLowerCase().replace(/-/g, '_');

function aggregateLiveEvents(rows, peak) {
  const byDate = new Map();
  for (const row of rows) {
    if (!row.start_local) continue;
    const date = londonDate(row.start_local);
    if (!byDate.has(date)) {
      byDate.set(date, {
        date,
        categories: Object.fromEntries(EVENT_CATEGORIES.map(([key]) => [key, 0])),
        eventsTotal: 0,
        eventsPeak: peak
      });
    }
    const day = byDate.get(date);
    const attendance = Number(row.attendance || 0);
    const key = categoryKey(row.category);
    if (key in day.categories) day.categories[key] += attendance;
    day.eventsTotal += attendance;
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

function DateRange({ filters, setFilters }) {
  const set = (name, value) => setFilters((current) => ({ ...current, [name]: value }));
  return <div className="demand-filters">
    <label>Start date<input type="date" value={filters.startDate} max={filters.endDate || undefined} onChange={(event) => set('startDate', event.target.value)} /></label>
    <label>End date<input type="date" value={filters.endDate} min={filters.startDate || undefined} onChange={(event) => set('endDate', event.target.value)} /></label>
  </div>;
}

function DemandToast({ loading, error }) {
  if (!loading && !error) return null;
  return <div className={`demand-toast${error ? ' demand-toast-error' : ''}`} role={error ? 'alert' : 'status'}>
    {error || 'Loading demand features...'}
  </div>;
}

export default function DemandModel() {
  const [flights, setFlights] = useState([]);
  const [events, setEvents] = useState([]);
  const [weather, setWeather] = useState([]);
  const [flightPeaks, setFlightPeaks] = useState({ lhr_peak: 1410, lgw_peak: 901, stn_peak: 677 });
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getDefaultDateRange);

  useEffect(() => {
    if (!filters.startDate || !filters.endDate) return undefined;
    const controller = new AbortController();
    const params = new URLSearchParams({ startDate: filters.startDate, endDate: filters.endDate });
    setLoading(true);
    setError('');

    const readWeatherFromSql = async () => {
      if (!supabase) return null;

      const { data: featureRows, error: featureError } = await supabase
        .from('feature_weather')
        .select('*')
        .gte('date', filters.startDate)
        .lte('date', filters.endDate)
        .order('date');

      if (featureError) throw new Error(`Weather read failed: ${featureError.message}`);
      if (!featureRows || featureRows.length === 0) return null;

      const { data: blockRows, error: blockError } = await supabase
        .from('weather_blocks')
        .select('*')
        .gte('date', filters.startDate)
        .lte('date', filters.endDate)
        .order('date')
        .order('block_hour');

      if (blockError) throw new Error(`Weather blocks read failed: ${blockError.message}`);

      const blocksByDate = new Map();
      for (const block of blockRows || []) {
        if (!blocksByDate.has(block.date)) blocksByDate.set(block.date, []);
        blocksByDate.get(block.date).push(block);
      }

      return featureRows.map((day) => ({
        ...day,
        blocks: blocksByDate.get(day.date) || []
      }));
    };

    const fetchWeatherFromWorker = async () => {
      const response = await fetch(`${DEMAND_MODEL_URL}/weather/read?${params}`, {
        signal: controller.signal
      });

      const body = await response.json();
      if (!response.ok) {
        if (body?.error) throw new Error(body.error);
        throw new Error('Weather fetch failed.');
      }

      return body.days || [];
    };

    const configRequest = supabase
      ? supabase.from('demand_flight_config').select('lhr_peak,lgw_peak,stn_peak,events_peak').eq('id', 1).maybeSingle()
      : Promise.resolve({ data: null, error: null });
    const liveEventsRequest = supabase
      ? supabase.from('live_events').select('start_local,category,attendance')
        .gte('start_local', `${filters.startDate}T00:00:00`)
        .lt('start_local', `${filters.endDate}T23:59:59.999`)
      : Promise.resolve({ data: [], error: null });

    Promise.all([
      fetch(`${DEMAND_MODEL_URL}/flights/read?${params}`, { signal: controller.signal }).then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'Flight demand read failed.');
        return body.days || [];
      }),
      readWeatherFromSql()
        .then((sqlWeather) => {
          if (sqlWeather && sqlWeather.length > 0) return sqlWeather;
          return fetchWeatherFromWorker();
        })
        .catch(() => []),
      liveEventsRequest,
      configRequest
    ]).then(([flightDays, weatherDays, liveEventsResult, configResult]) => {
      if (controller.signal.aborted) return;
      if (liveEventsResult.error) throw new Error(`Live events read failed: ${liveEventsResult.error.message}`);
      const peak = Number(configResult.data?.events_peak) || DEFAULT_EVENTS_PEAK;
      setFlights(flightDays);
      setWeather(weatherDays);
      setEvents(aggregateLiveEvents(liveEventsResult.data || [], peak));
      if (configResult.data) setFlightPeaks(configResult.data);
      setExpanded({});
    }).catch((fetchError) => {
      if (!controller.signal.aborted) setError(fetchError.message);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [filters.startDate, filters.endDate]);

  const flightsByDate = new Map(flights.map((day) => [day.date, day]));
  const eventsByDate = new Map(events.map((day) => [day.date, day]));
  const weatherByDate = new Map(weather.map((day) => [day.date, day]));
  const dates = Array.from(new Set([...flights, ...events, ...weather].map((day) => day.date))).sort();
  const flightPeak = Number(flightPeaks.lhr_peak || 0) + Number(flightPeaks.lgw_peak || 0) + Number(flightPeaks.stn_peak || 0);
  const toggle = (date, section) => setExpanded((current) => ({
    ...current,
    [`${date}:${section}`]: !current[`${date}:${section}`]
  }));

  return <section className="demand-panel">
    <div className="demand-header">
      <div><span className="eyebrow">FORECAST FEATURES</span><h2>Demand features</h2></div>
      <DateRange filters={filters} setFilters={setFilters} />
    </div>
    <DemandToast loading={loading} error={error} />
    {!loading && !error && dates.length > 0 && <div className="table-panel demand-table-panel"><div className="table-scroll"><table className="demand-table">
      <thead><tr><th>Date</th><th>Flights total</th><th>% peak</th><th>Events total</th><th>% peak</th><th>Rain uplift</th><th>Temp uplift</th></tr></thead>
      <tbody>{dates.map((date) => {
        const flight = flightsByDate.get(date);
        const event = eventsByDate.get(date);
        const day = weatherByDate.get(date);
        const eventsPeak = event?.eventsPeak || 0;
        const flightsExpanded = expanded[`${date}:flights`];
        const eventsExpanded = expanded[`${date}:events`];
        const weatherExpanded = expanded[`${date}:weather`];
        return <React.Fragment key={date}>
          <tr>
            <td className="mono">{date}</td>
            <td><button className="demand-value-button" onClick={() => toggle(date, 'flights')}>{formatNumber(flight?.flightsTotal)}</button></td>
            <td className="peak-value">{percentageOfPeak(flight?.flightsTotal, flightPeak)}</td>
            <td><button className="demand-value-button" onClick={() => toggle(date, 'events')}>{formatNumber(event?.eventsTotal)}</button></td>
            <td className="peak-value">{percentageOfPeak(event?.eventsTotal, eventsPeak)}</td>
            <td><button className="demand-value-button" onClick={() => toggle(date, 'weather')}>{formatUplift(day?.precipitation_uplift)}</button></td>
            <td><button className="demand-value-button" onClick={() => toggle(date, 'weather')}>{formatUplift(day?.temperature_uplift)}</button></td>
          </tr>
          {flightsExpanded && <tr className="demand-breakdown-row"><td colSpan="7"><div className="detail-lines"><div className="detail-line"><span>Flight breakdown</span><strong>Base</strong></div><div className="detail-line"><span>LHR</span><strong>{formatNumber(flight?.airports?.LHR) || 'None'}</strong></div><div className="detail-line"><span>LGW</span><strong>{formatNumber(flight?.airports?.LGW) || 'None'}</strong></div><div className="detail-line"><span>STN</span><strong>{formatNumber(flight?.airports?.STN) || 'None'}</strong></div></div></td></tr>}
          {eventsExpanded && <tr className="demand-breakdown-row"><td colSpan="7"><div className="detail-lines"><div className="detail-line"><span>Event attendance breakdown</span><strong>Base</strong></div>{EVENT_CATEGORIES.map(([key, label]) => <div className="detail-line" key={key}><span>{label}</span><strong>{formatNumber(event?.categories?.[key]) || 'None'}</strong></div>)}</div></td></tr>}
          {weatherExpanded && <tr className="demand-breakdown-row"><td colSpan="7"><div className="detail-lines"><div className="detail-line"><span>Weather breakdown</span><strong>{day?.created_at ? 'Updated' : 'None'}</strong></div>{day?.created_at && <div className="detail-line"><span>Updated</span><strong>{formatWeatherTimestamp(day.created_at)}</strong></div>}<div className="detail-line"><span>Rain</span><strong>{Number(day?.precipitation_total_mm || 0).toFixed(1)}mm</strong></div><div className="detail-line"><span>Rain intensity</span><strong>{formatUplift(day?.precipitation_intensity) || 'None'}</strong></div><div className="detail-line"><span>Temp</span><strong>{day?.temperature_min_c ?? '-'}°C to {day?.temperature_max_c ?? '-'}°C</strong></div><div className="detail-line"><span>Temp intensity</span><strong>{formatUplift(day?.temperature_intensity) || 'None'}</strong></div>{(day?.blocks || []).map((block) => <div className="detail-line" key={block.block_hour}><span>{String(block.block_hour).padStart(2, '0')}:00</span><strong>{Number(block.temperature_c ?? 0).toFixed(0)}°C / {Number(block.precipitation_mm || 0).toFixed(1)}mm</strong></div>)}</div></td></tr>}
        </React.Fragment>;
      })}</tbody>
    </table></div></div>}
    {!loading && !error && !dates.length && <div className="empty-state">No demand data for this date range.</div>}
  </section>;
}
